import { useCallback } from 'react';

import {
  addUploadToProject,
  alwaysCreateNewProject,
} from 'src/modules/homeownerApp/components/UploadImage';
import type { NewProjectArgs } from 'src/shared/graphql/generated';
import { usePublicOrgInfoLazyQuery } from 'src/shared/graphql/generated';
import { useIsEmbed } from 'src/shared/hooks';
import { useGlobalStorageContext } from 'src/shared/hooks/useGlobalStorage';

import { createAiOriginalImage } from '../services';

import { useHomeownerAppContext } from './useHomeownerAppContext';

export const useUploadOriginalImage = () => {
  const { setAffOrgPublicId, affOrgPublicId } = useGlobalStorageContext();
  const isEmbed = useIsEmbed();
  const [publicOrgInfoQuery] = usePublicOrgInfoLazyQuery({
    variables: {
      publicIdentifier: affOrgPublicId || '',
    },
  });
  const { trackingData } = useHomeownerAppContext();

  return useCallback(
    async (variables: {
      image: File;
      email?: string;
      spaceType?: string;
      marketingConsent?: string;
      jobClientIdentifier?: string | null;
    }) => {
      const addToProject = addUploadToProject();
      const createNewProject = alwaysCreateNewProject();
      let project: Partial<NewProjectArgs> | undefined;

      if (!isEmbed && affOrgPublicId) {
        const { data: publicOrgInfo } = await publicOrgInfoQuery();
        if (publicOrgInfo?.publicOrgInfo) {
          project = {};
          project.orgId = publicOrgInfo.publicOrgInfo.id;
          if (publicOrgInfo?.publicOrgInfo.displayName) {
            project.name = `My ${publicOrgInfo.publicOrgInfo.displayName} Project`;
          }
        }
        setAffOrgPublicId();
      }

      const result = await createAiOriginalImage(
        variables.image,
        {
          email: variables.email,
          spaceType: variables.spaceType,
          marketingConsent: variables.marketingConsent,
          jobClientIdentifier: variables.jobClientIdentifier,
          project,
          // do not pass in any project ids if we are creating a new project
          projectId:
            affOrgPublicId || createNewProject ? undefined : addToProject?.id,
        },
        trackingData,
        true,
      );

      return result;
    },
    [
      isEmbed,
      affOrgPublicId,
      trackingData,
      publicOrgInfoQuery,
      setAffOrgPublicId,
    ],
  );
};
